import React, { useState, useEffect, useMemo, useCallback } from 'react';

import { useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useConfig } from 'config';
import { FixedWidthButton, ArrayItem } from 'form/form.styles';
import Dialog from 'components/molecules/dialog';
import { Icon } from 'components/atoms';

import { Button, FieldSelect, FormControl, Loading } from 'components/atoms';
import { ButtonWrapper } from 'form/form.styles';
import {
  Column,
  ColumnsContainer,
  EditForm,
  FormHeader,
  Wrapper,
  ActionButton,
  AddInternalOwnerButton,
} from './ThirdPartyEditInternalOwners.styles';
import { ThirdPartyDataConfigDictionary } from 'services/getThirdPartyConfiguration';
import { useUserSettings, useTerminologyConfig } from 'context/DataContext';
import { ThirdPartyEditInternalOwnersResponse } from 'types/responses/thirdParty/thirdPartyEditInternalOwnersResponse';
import { ThirdPartyOwner } from 'types';
import { useFetch } from 'hooks';

interface ThirdPartyEditInternalOwnersProps {
  onCancel: () => void;
  onSave: (thirdParty: ThirdPartyEditInternalOwnersResponse) => void;
  id?: string;
  entityType?: string;
  thirdPartyOwners?: ThirdPartyOwner[];
  thirdPartyDataConfig?: ThirdPartyDataConfigDictionary;
  headers?: { [key: string]: string };
  inModal: boolean;
}

const ThirdPartyEditInternalOwners = ({
  onCancel,
  onSave,
  id,
  entityType,
  thirdPartyOwners,
  thirdPartyDataConfig,
  headers,
  inModal,
}: ThirdPartyEditInternalOwnersProps) => {
  const { gatewayUserId } = useUserSettings();
  const { vantageWebApi } = useConfig();
  const terminologyConfig = useTerminologyConfig();
  const [showDialog, setShowDialog] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);

  const {
    data: ownersOptions,
    loading: isOwnerLoading,
    error: hasOwnerError,
  } = useFetch(
    `${vantageWebApi}/thirdParty/owners?screeningType=${entityType}`,
    { method: 'GET', headers },
    'json'
  );

  const {
    handleSubmit,
    register,
    control,
    getValues,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    mode: 'all',
    resolver: yupResolver(
      yup.object().shape({
        ownerIds: yup
          .array()
          .test(
            'duplicatedOwnerIds',
            `The same user has been selected as an ${terminologyConfig?.internalOwnerSingularNaming} more than once. Please remove duplication`,
            (ownerIds) => {
              const ownerIdsToCheck: string[] = ownerIds
                .filter(({ value }) => value !== 'DEFAULT')
                .map((obj) => obj.value);

              return new Set(ownerIdsToCheck).size === ownerIdsToCheck.length;
            }
          ),
      })
    ),
    defaultValues: {
      ownerIds: [],
    },
  });

  const {
    fields: ownerFields,
    append: ownerAppend,
    remove: ownerRemove,
  } = useFieldArray({
    control,
    name: 'ownerIds',
  });

  const parseData = (data) => {
    data.ownerIds = data?.ownerIds
      ?.filter(({ value }) => value !== 'DEFAULT')
      .map(({ value }) => value);

    return JSON.stringify(data);
  };

  const postEdit = async (data) => {
    const editResponse = await fetch(`${vantageWebApi}/thirdParty/${id}/owners/edit`, {
      method: 'PUT',
      headers,
      body: parseData(data),
    });

    if (!editResponse.ok) {
      //setSubmitError(true);
    }

    return await editResponse.json();
  };

  useEffect(() => {
    if (!thirdPartyDataConfig) return;

    const defaultOwnerId = thirdPartyDataConfig?.autoAssignDefaultInternalOwner?.configurationValue;
    let ownersData = thirdPartyOwners ?? [];
    if (defaultOwnerId) {
      const defaultOwner = ownersData.find((owner) => String(owner.users.id) === defaultOwnerId);
      const otherOwners = ownersData.filter((owner) => String(owner.users.id) !== defaultOwnerId);

      ownersData = defaultOwner ? [defaultOwner, ...otherOwners] : thirdPartyOwners;
    }
    setValue(
      'ownerIds',
      ownersData?.map(({ users }) => ({
        value: String(users?.id) || 'DEFAULT',
      })) || []
    );
  }, [thirdPartyDataConfig, reset, vantageWebApi, setValue, thirdPartyOwners]);

  const permissionToRemoveThemselvesEnabled = useCallback(
    (ownerId: string): boolean => {
      return !(
        thirdPartyDataConfig?.allowRemoveMyselfAsOwner?.isEnabled === false &&
        ownerId === gatewayUserId
      );
    },
    [thirdPartyDataConfig, gatewayUserId]
  );

  const isDefaultOwner = useCallback(
    (ownerId: string, index: number) => {
      return (
        ownerId === thirdPartyDataConfig?.autoAssignDefaultInternalOwner?.configurationValue &&
        index === 0
      );
    },
    [thirdPartyDataConfig]
  );

  const showAddOwnersButton = useMemo(() => {
    return thirdPartyDataConfig?.allowAddOtherOwners?.isEnabled && ownerFields.length < 3;
  }, [thirdPartyDataConfig, ownerFields]);

  const onSubmit = () => setShowDialog(true);

  const handleAddOwner = () => {
    ownerAppend({ value: 'DEFAULT' });
  };

  const handleRemoveOwner = (index) => {
    ownerRemove(index);
  };

  const handleConfirmDialog = async () => {
    setShowDialog(true);
    const serverData = await postEdit(getValues());
    onSave(serverData);
    setIsSubmitLoading(false);
    setShowDialog(false);
  };

  const handleDeclineDialog = () => {
    setShowDialog(false);
  };

  if (hasOwnerError) {
    return <h1> ERROR </h1>;
  }
  return (
    <Wrapper>
      {isOwnerLoading ? (
        <Loading />
      ) : (
        <>
          <FormHeader hidden={inModal}>
            {`Edit ${terminologyConfig?.internalOwnerSingularNaming}`}
          </FormHeader>
          <EditForm onSubmit={handleSubmit(onSubmit)}>
            <ColumnsContainer>
              <Column>
                <FormControl>
                  {ownerFields.map((field, index) => {
                    return (
                      <ArrayItem key={field.id}>
                        <FormControl
                          style={{ flex: 1 }}
                          errorMessage={(errors?.ownerIds as any)?.message}
                        >
                          <FieldSelect
                            label={`${terminologyConfig?.internalOwnerSingularNaming}`}
                            id={`ownerIds.${index}.value`}
                            key={`ownerIds.${index}.value`}
                            register={register}
                            options={ownersOptions?.map((owner) => ({
                              name: `${owner.firstName} ${owner.lastName}${
                                owner.company ? ` (${owner.company})` : ''
                              }`,
                              value: owner.id,
                            }))}
                            placeholder={`Please select an ${terminologyConfig?.internalOwnerSingularNaming.toLowerCase()}`}
                            placeholderValue="DEFAULT"
                            fullWidth
                            disabled={
                              !permissionToRemoveThemselvesEnabled(field.value) ||
                              isDefaultOwner(field.value, index)
                            }
                          />
                        </FormControl>
                        {permissionToRemoveThemselvesEnabled(field.value) &&
                          !isDefaultOwner(field.value, index) && (
                            <ActionButton type="button" onClick={() => handleRemoveOwner(index)}>
                              <Icon icon="trash" size="xs" />
                            </ActionButton>
                          )}
                      </ArrayItem>
                    );
                  })}
                  {showAddOwnersButton && (
                    <ButtonWrapper
                      style={{
                        justifyContent: 'flex-end',
                        marginTop: '0px',
                        padding: '0px',
                      }}
                    >
                      <AddInternalOwnerButton type="button" onClick={handleAddOwner}>
                        <Icon className="icon" icon="plus-circle" size="xs" />
                        Add another&nbsp;
                        {terminologyConfig?.internalOwnerSingularNaming}
                      </AddInternalOwnerButton>
                    </ButtonWrapper>
                  )}
                </FormControl>
              </Column>
            </ColumnsContainer>

            <ButtonWrapper style={{ justifyContent: 'flex-end', width: '100%' }}>
              <Button text type="button" disabled={isSubmitLoading} onClick={onCancel}>
                Cancel
              </Button>
              <FixedWidthButton secondary type="submit" disabled={isSubmitLoading}>
                Save
              </FixedWidthButton>
            </ButtonWrapper>
          </EditForm>
        </>
      )}
      <Dialog isOpen={showDialog} onConfirm={handleConfirmDialog} onDecline={handleDeclineDialog}>
        <p>Any changes will not be applied to any completed or in progress Due Diligence reports</p>
      </Dialog>
    </Wrapper>
  );
};

export default ThirdPartyEditInternalOwners;
